<template>
  <v-card
    class="shared-agent-card"
    color="background"
    variant="elevated"
    elevation="4"
    @click="onCardClick"
  >
    <div class="agent-desktop-shell">
      <div class="agent-desktop-header">
        <div class="agent-desktop-header-main">
          <v-avatar size="48" rounded="lg" class="agent-avatar agent-icon-tile">
            <v-icon :icon="iconName" size="small"></v-icon>
          </v-avatar>
          <div class="agent-title-wrap">
            <div class="agent-title-row">
              <div class="agent-title">{{ agent.agentName }}</div>
              <v-chip
                v-if="agent.isPublished"
                size="small"
                color="success"
                variant="tonal"
                class="agent-live-chip"
              >
                LIVE
              </v-chip>
            </div>
            <div v-if="agent.ownerUsername" class="shared-owner-line">
              Shared by @{{ agent.ownerUsername }}
            </div>
          </div>
        </div>
      </div>

      <div class="agent-description">
        {{ agent.description }}
      </div>

      <div class="agent-chip-row">
        <v-chip size="small" variant="tonal" class="agent-chip">
          <v-icon icon="mdi-earth" size="x-small" start></v-icon>
          {{ modelChipLabel }}
        </v-chip>
        <v-chip size="small" variant="outlined" class="agent-chip">
          <v-icon icon="mdi-tools" size="x-small" start></v-icon>
          {{ toolsChipLabel }}
        </v-chip>
      </div>

      <div class="agent-footer">
        <div class="agent-footer-spacer"></div>
        <div class="agent-actions">
          <v-tooltip v-if="publicUrl" text="Open in new window" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="secondary"
                icon="mdi-open-in-new"
                size="small"
                variant="plain"
                :href="publicUrl"
                target="_blank"
                @click.stop
              />
            </template>
          </v-tooltip>
          <v-tooltip v-if="agent.isPublished && agent.slug" text="Chat with Agent" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                icon="mdi-chat-outline"
                size="small"
                variant="plain"
                @click.stop="goToChat"
              />
            </template>
          </v-tooltip>
          <v-tooltip v-if="agent.editable" text="Edit Agent" location="top">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                icon="mdi-pencil-outline"
                size="small"
                variant="plain"
                @click.stop="emitEdit"
              />
            </template>
          </v-tooltip>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { SharedAgentInfo } from '@/types/agents'
import { resolveAgentIcon } from '@/utils/agentIcon'
import { normalizeModelPricingLookupModel } from '@/utils/modelPricing'
import { getDashboardTabPath } from '@/router/dashboardTabs'

@Options({
  props: {
    agent: Object
  },
  emits: ['edit']
})
export default class SharedAgentCard extends Vue {
  agent!: SharedAgentInfo

  get iconName(): string {
    return resolveAgentIcon(this.agent?.icon)
  }

  get publicUrl(): string {
    if (!this.agent?.isPublished || !this.agent.slug || !this.agent.ownerUsername) return ''
    if (typeof window === 'undefined') return ''
    return `${window.location.origin}/agent/${this.agent.ownerUsername}/${this.agent.slug}`
  }

  get modelChipLabel(): string {
    return normalizeModelPricingLookupModel(this.agent?.modelName || 'No model')
  }

  get toolsChipLabel(): string {
    const total = this.agent?.toolsCount ?? 0
    return `${total} tools`
  }

  goToChat(): void {
    if (!this.agent?.slug) return
    this.$store.dispatch('setPendingChatAgent', {
      slug: this.agent.slug,
      ownerUsername: this.agent.ownerUsername
    })
    this.$store.dispatch('setTab', 'chat')
    const path = getDashboardTabPath('chat')
    if (this.$route.path !== path) {
      this.$router.push(path)
    }
  }

  emitEdit(): void {
    this.$emit('edit', this.agent)
  }

  onCardClick(): void {
    if (this.agent?.editable) {
      this.emitEdit()
    }
  }
}
</script>

<style scoped lang="scss">
.shared-agent-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  height: 100%;
  min-height: 214px;
}

.agent-desktop-shell {
  padding: 16px 16px 12px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.agent-desktop-header {
  display: flex;
  align-items: flex-start;
}

.agent-desktop-header-main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
}

.agent-avatar {
  flex-shrink: 0;
}

.agent-icon-tile {
  background-color: #1f1d16;
  color: #fff;
}

.agent-title-wrap {
  min-width: 0;
}

.agent-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.agent-title {
  font-size: 1.05rem;
  line-height: 1.15;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-live-chip {
  letter-spacing: 0.03em;
  flex-shrink: 0;
}

.shared-owner-line {
  margin-top: 2px;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.62);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.agent-description {
  margin-top: 10px;
  font-size: 0.9rem;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.62);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.55em;
}

.agent-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.agent-chip {
  font-size: 0.78rem;
}

.agent-footer {
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  display: flex;
  justify-content: flex-end;
}

.agent-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}
</style>
